import {
  COMPANY_SEARCH_API_METADATA_PREFIX,
  PEOPLE_SEARCH_API_METADATA_PREFIX,
} from '@aibees/elephant/constants';
import { SearchType } from '@aibees/elephant/types';
import { HttpMethod } from '@aibees/shared/definition';
import { useDebounceFn } from '@aibees/shared/hooks';
import { useBaseQuery } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback } from 'react';
import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import {
  BrosweLabelsByIdsRequest,
  BrosweLabelsByIdsResponse,
  LabelUpdatesSocketMessage,
} from './types';
import { useSocketLabelUpdateListener } from './use-socket-label-update-listener';

let globalDebounceTimeout: NodeJS.Timeout | null = null;

export function useBrosweLabelsByIds(
  request: BrosweLabelsByIdsRequest,
  enabled?: boolean
) {
  const workspace = useWorkspace()?.id;
  const type = useSearchTypeFromRoute();

  let url = COMPANY_SEARCH_API_METADATA_PREFIX;
  if (type === SearchType.People) url = PEOPLE_SEARCH_API_METADATA_PREFIX;

  const api = useBaseQuery<BrosweLabelsByIdsRequest, BrosweLabelsByIdsResponse>(
    [useBrosweLabelsByIds.name, type, request],
    {
      url: url?.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      params: { return: 'label' },
      data: request,
    },
    { enabled }
  );

  const debouncedRefetch = useCallback(() => {
    if (globalDebounceTimeout) {
      clearTimeout(globalDebounceTimeout);
    }
    globalDebounceTimeout = setTimeout(() => {
      if (request.ids.length > 0) {
        api.refetch();
      }
      globalDebounceTimeout = null;
    }, 1000); // Adjust the delay as needed.
  }, [api, request.ids]);

  const onLabelUpdateEvent = useDebounceFn(
    (_data: LabelUpdatesSocketMessage) => {
      debouncedRefetch();
    },
    1000
  );

  useSocketLabelUpdateListener(onLabelUpdateEvent);

  return api;
}
